/**
 * ReminderWatcher — runs globally (mounted in App.jsx).
 * Polls every 30 seconds; when a reminder is due it shows a popup.
 * Works no matter which page the user is on.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { remindersService } from '../../services/remindersService';
import './Reminders.css';

const SNOOZE_OPTIONS = [
  { label: '15 min',  minutes: 15 },
  { label: '30 min',  minutes: 30 },
  { label: '1 hour',  minutes: 60 },
  { label: '2 hours', minutes: 120 },
];

const POLL_INTERVAL_MS = 30_000; // 30 seconds

// Shared AudioContext — created once, unlocked on first user gesture
let sharedAudioCtx = null;

function getAudioCtx() {
  if (!sharedAudioCtx) {
    sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  return sharedAudioCtx;
}

// Called on every user interaction so the context is always unlocked
function unlockAudio() {
  try {
    const ctx = getAudioCtx();
    if (ctx.state === 'suspended') ctx.resume();
  } catch (_) {}
}

// 3-note ascending chime: C5 → E5 → G5
function playReminderSound() {
  try {
    const ctx = getAudioCtx();
    if (ctx.state === 'suspended') ctx.resume();

    const notes = [523.25, 659.25, 783.99];
    [0, 1].forEach(repeat => {
      const repeatOffset = repeat * 0.9;
      notes.forEach((freq, i) => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = freq;
        const t = ctx.currentTime + repeatOffset + i * 0.22;
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.35, t + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 1.1);
        osc.start(t);
        osc.stop(t + 1.1);
      });
    });
  } catch (_) {}
}

export default function ReminderWatcher({ user }) {
  const navigate = useNavigate();
  const [dueReminders, setDueReminders] = useState([]);  // queue of due items
  const [current, setCurrent] = useState(null);           // the one showing now
  const [snoozing, setSnoozing] = useState(false);
  const dismissedRef = useRef(new Set());                 // IDs dismissed this session
  const isFetchingRef = useRef(false);                    // prevents overlapping polls

  const username = user?.UserName;

  // Unlock AudioContext on first user interaction (browser autoplay policy)
  useEffect(() => {
    const events = ['click', 'keydown', 'touchstart'];
    events.forEach(e => document.addEventListener(e, unlockAudio, { once: true, passive: true }));
    return () => events.forEach(e => document.removeEventListener(e, unlockAudio));
  }, []);

  const checkDue = useCallback(async () => {
    if (!username) return;
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      const all = await remindersService.getAll(username);
      const now = new Date();

      const due = all.filter(r => {
        if (r.IsCompleted || !r.ReminderTime) return false;
        if (dismissedRef.current.has(r.ReminderID)) return false;

        const fireAt = r.SnoozeUntil ? new Date(r.SnoozeUntil) : new Date(r.ReminderTime);
        return fireAt <= now;
      });

      setDueReminders(due);
    } catch (err) {
      // silently ignore polling errors
    } finally {
      isFetchingRef.current = false;
    }
  }, [username]);

  // Kick off polling
  useEffect(() => {
    checkDue();
    const interval = setInterval(checkDue, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [checkDue]);

  // Promote first queued item to the visible popup
  useEffect(() => {
    if (!current && dueReminders.length > 0) {
      const [next, ...rest] = dueReminders;
      setCurrent(next);
      setDueReminders(rest);
      playReminderSound();

      // Try sending SMS (best-effort, no UI feedback needed)
      if (!next.SMSSent) {
        remindersService.sendSMS(next.ReminderID, username).catch(() => {});
      }
    }
  }, [current, dueReminders, username]);

  const dismiss = () => {
    if (!current) return;
    dismissedRef.current.add(current.ReminderID);
    setCurrent(null);
  };

  const markDone = async () => {
    if (!current) return;
    try {
      await remindersService.update(current.ReminderID, username, { isCompleted: true });
    } catch (_) {}
    dismissedRef.current.add(current.ReminderID);
    setCurrent(null);
  };

  const snooze = async (minutes) => {
    if (!current) return;
    setSnoozing(true);
    try {
      await remindersService.snooze(current.ReminderID, username, minutes);
    } catch (_) {}
    dismissedRef.current.add(current.ReminderID);
    setSnoozing(false);
    setCurrent(null);
  };

  if (!current) return null;

  const timeStr = new Date(current.ReminderTime).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
  });

  return (
    <div className="reminder-popup-overlay">
      <div className="reminder-popup">
        <div className="reminder-popup-icon">
          <i className="fas fa-bell"></i>
        </div>

        <div className="reminder-popup-content">
          <h3 className="reminder-popup-title">{current.Title}</h3>
          {current.Notes && <p className="reminder-popup-notes">{current.Notes}</p>}
          <p className="reminder-popup-time">
            <i className="fas fa-clock"></i> {timeStr}
          </p>

          {(current.LinkedOrderNo || current.LinkedCustomerName) && (
            <div className="link-badges reminder-popup-links">
              {current.LinkedOrderNo && (
                <button className="link-badge" onClick={() => { dismiss(); navigate(`/work-order-profile/${current.LinkedOrderNo}`); }}>
                  <i className="fas fa-file-alt"></i> Order #{current.LinkedOrderNo}
                </button>
              )}
              {current.LinkedCustomerName && current.LinkedCustomerID && (
                <button className="link-badge" onClick={() => { dismiss(); navigate(`/customers/${current.LinkedCustomerID}`); }}>
                  <i className="fas fa-user"></i> {current.LinkedCustomerName}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="reminder-popup-snooze-label">Snooze for:</div>
        <div className="reminder-popup-snooze-options">
          {SNOOZE_OPTIONS.map(opt => (
            <button
              key={opt.minutes}
              className="snooze-btn"
              onClick={() => snooze(opt.minutes)}
              disabled={snoozing}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="reminder-popup-actions">
          <button className="popup-btn popup-btn-done" onClick={markDone}>
            <i className="fas fa-check"></i> Mark Done
          </button>
          <button className="popup-btn popup-btn-dismiss" onClick={dismiss}>
            Dismiss
          </button>
        </div>

        {dueReminders.length > 0 && (
          <p className="reminder-popup-queue">
            +{dueReminders.length} more reminder{dueReminders.length > 1 ? 's' : ''} waiting
          </p>
        )}
      </div>
    </div>
  );
}
